import { Logger, Presenter, type IPresenterOwner } from 'wdc-cube'
import type { MainPresenter } from 'src/main/Main.presenter'
import FilterManager, {
    NumberFilterToKey,
    ObjectProperty,
    ValueProperty
} from '../the-consumer-market/tcm_filter_manager'
import { summarizeFilterScope } from '../the-consumer-market/tcm_filter_summary'
import {
    DateFilterScope,
    JunctionMode,
    MultipleOptionStringKeywordFilterScope,
    NumberFilterScope,
    SelectFilterScope,
    StringFilterScope,
    StringKeywordFilterScope,
    type AppliedFilterSummary,
    type IFilterScope,
    type KeywordOption
} from '../the-consumer-market/tcm_scopes'
import {
    SelectOperator,
    StringKeywordOperator,
    type DateRange,
    type EmpresaCodeFilter,
    type EmpresaFilter,
    type FilterSelectOption,
    type MultipleStringKeywordFilter,
    type NumberFilter,
    type SelectFilter,
    type StringFilter
} from '../the-consumer-market/tcm_service'
import { type FilterPaneOwner } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_types'
import { TcmPresenterForFilterCatalog } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_catalog_presenter'
import { TcmPresenterFilterForCnpj } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_cnpj'
import { TcmPresenterForDateFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_date'
import { TcmPresenterForNumberFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_number'
import { TcmPresenterForStringFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_string'
import { TcmPresenterForStringMultiSelectFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_presenter_for_string_multi_select'
import { TcmPresenterForSelectFilter } from '../the-consumer-market/tcm_presenter_filter_pane/tcm_filter_select_presenter'
import {
    filterCaptionMatches,
    loadAllCities,
    loadAllUFs
} from '../the-consumer-market/tcm_presenter_filter_pane/tcm_presenter_filter_pane'
import { MvFilterCatalogFormKeys, MvFilterCatalogShareKeys } from './mv_keys'
import { MvFilterPaneScope } from './mv_scopes'
import { MercadoVetService, type CnpjOpcoes, type WebOpcoes } from './mv_service'
import type { CnaeOrdem, VetCamposFilter, VetFilter, WebFilter } from './mv_types'
import { normalizeWord } from './mv_utils'

const LOG = Logger.get('MV-FilterPane')

const service = MercadoVetService.singleton()

export type FilterPaneHost = IPresenterOwner & {
    fetchData(source: ''): Promise<unknown>
    onFiltersCleared?(): void
}

type FilterItem = {
    readonly scope: IFilterScope
    publish(): void
    clear(): void
    release(): void
    apply(values: unknown): void
}

const SIM_NAO_OPTIONS: FilterSelectOption[] = [
    { id: 'sim', title: 'Sim' },
    { id: 'nao', title: 'Não' }
]

abstract class MvFilterPanePresenter<H extends FilterPaneHost>
    extends Presenter<MvFilterPaneScope, H>
    implements FilterPaneOwner
{
    constructor(owner: H, scope: MvFilterPaneScope) {
        super(owner, scope)
        this.filterManager.bindIsAdditiveModeOn(() => true)
    }

    readonly filterManager = new FilterManager()
    private readonly __items: FilterItem[] = []
    private readonly __keys = new Map<FilterItem, string>()
    private __order = 0

    // :: Lifecycle

    async initialize() {
        this.scope.onToggleExpanded = this.action(this.__handleToggleExpanded)
        this.scope.onClearFilters = this.action(this.__handleClearFilters)
        this.scope.onApply = this.action(this.__handleApply)
        this.scope.onSearchFilterChanged = this.action(this.__handleSearchFilterChanged)

        await this.prepareFilters()
        this.scope.filters = this.__items.map((item) => item.scope)
    }

    override release() {
        for (const item of this.__items) item.release()
        super.release()
    }

    protected abstract prepareFilters(): Promise<void>

    protected restoreDefaults() {
        // NOOP
    }

    protected hasExtraFilter() {
        return false
    }

    protected afterPublish() {
        // NOOP
    }

    protected async add<P extends FilterItem & { prepare(order: number, label: string): Promise<void> }>(
        presenter: P,
        caption: string,
        key?: string
    ) {
        await presenter.prepare(this.__order++, caption)
        this.__items.push(presenter)
        key && this.__keys.set(presenter, key)
        return presenter
    }

    protected async addSelect(
        presenter: TcmPresenterForSelectFilter,
        caption: string,
        options: FilterSelectOption[],
        key?: string
    ) {
        await presenter.prepareFilter(this.__order++, caption, options)
        this.__items.push(presenter)
        key && this.__keys.set(presenter, key)
        return presenter
    }

    protected newSelectProperty() {
        return new ObjectProperty<SelectFilter>(this.filterManager)
    }

    protected newNumberProperty() {
        return new ObjectProperty<NumberFilter>(this.filterManager, false, NumberFilterToKey)
    }

    protected newStringProperty() {
        return new ValueProperty<StringFilter>(this.filterManager)
    }

    protected newMultipleProperty() {
        return new ValueProperty<MultipleStringKeywordFilter>(this.filterManager)
    }

    protected selecionadasNaLista(prop: ValueProperty<MultipleStringKeywordFilter>): Set<string> {
        const result = new Set<string>()
        for (const filter of prop.values()) {
            if (filter.operator !== StringKeywordOperator.is_in || !Array.isArray(filter.value)) continue
            for (const v of filter.value) result.add(normalizeWord(String(v)))
        }
        return result
    }

    protected toggleNaLista(presenter: TcmPresenterForStringMultiSelectFilter, texto: string) {
        const chave = normalizeWord(texto)
        const condicao = presenter.getOrCreateIsIn()
        const marcadas = condicao.value.filter((v) => normalizeWord(v) !== chave)
        if (marcadas.length === condicao.value.length) {
            const opcao = condicao.options.find((o) => normalizeWord(o.key) === chave)
            if (!opcao) condicao.options = [...condicao.options, { key: texto, label: texto }]
            marcadas.push(opcao?.key ?? texto)
        }
        condicao.value = marcadas
        presenter.publish()
    }

    protected lista<T>(prop: { size: number; values(): IterableIterator<T> }) {
        return prop.size > 0 ? [...prop.values()] : undefined
    }

    protected applyItems(data: Record<string, unknown>) {
        for (const item of this.__items) item.clear()
        this.filterManager.clear()
        for (const [item, key] of this.__keys) {
            const value = key
                .split('.')
                .reduce<unknown>(
                    (obj, k) => (obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[k] : undefined),
                    data
                )
            if (value !== undefined) item.apply(value)
        }
    }

    protected sortItemsByCaption(from: number) {
        const fixos = this.__items.slice(0, from)
        const resto = this.__items
            .slice(from)
            .sort((a, b) => (a.scope.caption ?? '').localeCompare(b.scope.caption ?? '', 'pt-BR'))
        this.__items.splice(0, this.__items.length, ...fixos, ...resto)
        this.__items.forEach((item, index) => (item.scope.order = index))
    }

    // :: API

    publish() {
        for (const item of this.__items) item.publish()
        this.scope.isFiltering = this.filterManager.hasFilter() || this.hasExtraFilter()
        this.afterPublish()
    }

    describeAppliedFilters(): AppliedFilterSummary[] {
        const result: AppliedFilterSummary[] = []
        for (const item of this.__items) {
            const summary = summarizeFilterScope(item.scope)
            summary && result.push(summary)
        }
        return result
    }

    // :: Handlers

    private async __handleToggleExpanded() {
        this.scope.expanded = !this.scope.expanded
    }

    private async __handleClearFilters() {
        for (const item of this.__items) item.clear()
        this.filterManager.clear()
        this.restoreDefaults()
        this.owner.onFiltersCleared?.()
        await this.owner.fetchData('')
    }

    private async __handleApply() {
        await this.owner.fetchData('')
    }

    private async __handleSearchFilterChanged(text: string) {
        this.scope.searchFilterText = text
        for (const item of this.__items) {
            item.scope.visible = filterCaptionMatches(text, item.scope.caption)
        }
    }
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Aba "Mercado Veterinário" (base CNPJ)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

const FONTE_DOMINIO_OPTIONS: KeywordOption[] = [
    { key: 'site_2023', label: 'Site (2023)' },
    { key: 'registro_br', label: 'Registro.br' },
    { key: 'google', label: 'Google Maps' }
]

const CNAE_ORDEM_OPTIONS: FilterSelectOption[] = [
    { id: 'primario', title: 'Primário' },
    { id: 'secundario', title: 'Secundário' }
]

const CNAE_ORDEM_KEY = 'vetCnaeOrdem'

type ManagerKey<T> = { [K in keyof FilterManager]: FilterManager[K] extends T ? K : never }[keyof FilterManager]

const COLUNAS_NUMERO: Array<[ManagerKey<ObjectProperty<NumberFilter>>, string]> = [
    ['idadeEmpresaEmAnos', 'Idade (anos)'],
    ['totalEmpresasNoGrupo', 'Empresas no grupo'],
    ['capitalSocial', 'Capital Social'],
    ['totalFiliais', 'Total de filiais'],
    ['salarioTotal', 'Salário Total'],
    ['salarioTotalGrupo', 'Salário Total Grupo'],
    ['webNota', 'Nota Google'],
    ['webQtdAvaliacoes', 'Avaliações Google'],
    ['webTempoMedio', 'Tempo médio (Google)'],
    ['enderecoLatitude', 'Latitude'],
    ['enderecoLongitude', 'Longitude']
]

const COLUNAS_TEXTO: Array<[ManagerKey<ValueProperty<StringFilter>>, string]> = [
    ['empresaNomeMatriz', 'Nome - Matriz'],
    ['naturezaJuridicaTexts', 'Natureza Jurídica'],
    ['enderecoBairroTexts', 'Bairro'],
    ['telefone1', 'Telefone 1 (RF)'],
    ['telefone2', 'Telefone 2 (RF)'],
    ['webSite', 'Site Google'],
    ['webNomeTexts', 'Nome Google'],
    ['webCategoriasTexts', 'Categorias Google'],
    ['webTelefone', 'Telefone Google']
]

const COLUNAS_DATA: Array<[ManagerKey<ObjectProperty<DateRange>>, string]> = [
    ['inicioAtividade', 'Início Atividade'],
    ['dtSituacaoCadastral', 'Data Situação']
]

const COLUNAS_CNPJ: Array<[ManagerKey<ValueProperty<EmpresaCodeFilter>>, string]> = [
    ['empresaCnpjMatriz', 'CNPJ Matriz'],
    ['empresaCnpjGrupo', 'CNPJ Grupo']
]

const MATRIZ_FILIAL_OPTIONS: FilterSelectOption[] = [
    { id: '1', title: 'Matriz' },
    { id: '0', title: 'Filial' }
]

const OPTANTE_SIMPLES_OPTIONS: KeywordOption[] = [
    { key: 'S', label: 'Sim' },
    { key: 'N', label: 'Não' }
]

const VET_COLUNAS_TEXTO: Array<[string, string]> = [
    ['razaoSocialMatriz', 'Razão Social - Matriz'],
    ['cnaeDescricao', 'Descrição CNAE'],
    ['endereco', 'Endereço'],
    ['cep', 'CEP'],
    ['fax', 'Telefone Fax (RF)'],
    ['emailRf', 'E-mail (RF)'],
    ['socios', 'Nome dos sócios'],
    ['emailSite', 'E-mail (site)'],
    ['telefoneSite', 'Telefone (site)']
]

const VET_COLUNAS_LISTA: Array<[string, string]> = [
    ['situacao', 'Situação Cadastral'],
    ['porte', 'Porte'],
    ['naturezaAgrupamento', 'Natureza Jurídica - Agrupamento'],
    ['qualificacaoResponsavel', 'Qualificação do Responsável'],
    ['motivoSituacao', 'Motivo da Situação'],
    ['situacaoEspecial', 'Situação Especial']
]

export type CnpjFilterHost = FilterPaneHost & {
    readonly app: MainPresenter
    buildFilter(): VetFilter
    onSavedFilterApplied(): Promise<void>
}

export class MvCnpjFilterPanePresenter extends MvFilterPanePresenter<CnpjFilterHost> {
    constructor(owner: CnpjFilterHost, scope: MvFilterPaneScope) {
        super(owner, scope)
        const app = owner.app
        this.catalog = new TcmPresenterForFilterCatalog(this, {
            app,
            module: 'mv-cnpj',
            buildData: () => this.buildSavedFilter(),
            openForm: (mode, sourceId) => new MvFilterCatalogFormKeys(app).setMode(mode).setSourceId(sourceId).flip(),
            openShare: (sourceId) => new MvFilterCatalogShareKeys(app).setSourceId(sourceId).flip()
        })
    }

    readonly catalog: TcmPresenterForFilterCatalog

    private readonly __cnaeOrdemProp = this.newSelectProperty()
    private __cnaeOrdem?: TcmPresenterForSelectFilter

    private readonly __secundarias = this.newMultipleProperty()
    private __secundariasPresenter?: TcmPresenterForStringMultiSelectFilter
    private readonly __cnaePrincipal = this.newMultipleProperty()
    private readonly __dominio = this.newStringProperty()
    private readonly __fonteDominio = this.newMultipleProperty()
    private readonly __email = this.newStringProperty()
    private readonly __telefone = this.newStringProperty()
    private readonly __midiaSocial = this.newSelectProperty()
    private readonly __palavras = this.newMultipleProperty()
    private __palavrasPresenter?: TcmPresenterForStringMultiSelectFilter
    private readonly __colunasTexto = new Map(VET_COLUNAS_TEXTO.map(([key]) => [key, this.newStringProperty()]))
    private readonly __colunasLista = new Map(VET_COLUNAS_LISTA.map(([key]) => [key, this.newMultipleProperty()]))

    private __opcoes?: Promise<CnpjOpcoes>

    override release() {
        this.catalog.release()
        super.release()
    }

    protected override async prepareFilters() {
        const mgr = this.filterManager

        this.__cnaeOrdem = await this.addSelect(
            new TcmPresenterForSelectFilter(this, new SelectFilterScope(), this.__cnaeOrdemProp),
            'CNAE 7500100 - Ordem',
            CNAE_ORDEM_OPTIONS,
            CNAE_ORDEM_KEY
        )
        this.restoreDefaults()

        this.__secundariasPresenter = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__secundarias,
                async () => (await this.__loadOpcoes()).secundarias
            ),
            'Atividades Secundárias',
            'secundarias'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__cnaePrincipal,
                async () => (await this.__loadOpcoes()).cnaes
            ),
            'CNAE Principal',
            'cnaePrincipal'
        )
        await this.add(
            new TcmPresenterFilterForCnpj(this, new StringKeywordFilterScope(), mgr.empresaCodigo),
            'CNPJ',
            'empresaCodigo'
        )
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), this.__dominio),
            'Domínio',
            'dominio'
        )
        await this.add(new TcmPresenterForStringFilter(this, new StringFilterScope(), this.__email), 'E-mail', 'email')
        await this.add(
            new TcmPresenterForNumberFilter(this, new NumberFilterScope(), mgr.faturamentoEstimado),
            'Faturamento Estimado da Empresa',
            'faturamentoEstimado'
        )
        await this.add(
            new TcmPresenterForNumberFilter(this, new NumberFilterScope(), mgr.faturamentoEstimadoGrupo),
            'Faturamento Estimado do Grupo',
            'faturamentoEstimadoGrupo'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__fonteDominio,
                async () => FONTE_DOMINIO_OPTIONS
            ),
            'Fonte do domínio',
            'fonteDominio'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                mgr.enderecoCidadeCodes,
                loadAllCities
            ),
            'Município',
            'enderecoCidadeCodes'
        )
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), mgr.empresaRazaoSocial, (texto) =>
                this.__sugestoes('razaoSocial', texto)
            ),
            'Nome Empresa',
            'empresaRazaoSocial'
        )
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), mgr.empresaNomeFantasia, (texto) =>
                this.__sugestoes('nomeFantasia', texto)
            ),
            'Nome Fantasia',
            'empresaNomeFantasia'
        )
        this.__palavrasPresenter = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__palavras,
                async () => (await this.__loadOpcoes()).palavras
            ),
            'Palavras em Destaque',
            'palavras'
        )
        await this.addSelect(
            new TcmPresenterForSelectFilter(this, new SelectFilterScope(), this.__midiaSocial),
            'Possui mídia social',
            SIM_NAO_OPTIONS,
            'midiaSocial'
        )
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), this.__telefone),
            'Telefone',
            'telefone'
        )
        await this.add(
            new TcmPresenterForNumberFilter(this, new NumberFilterScope(), mgr.totalFuncionarios),
            'Total Funcionários',
            'totalFuncionarios'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                mgr.enderecoUfs,
                loadAllUFs
            ),
            'UF',
            'enderecoUfs'
        )
        await this.__prepareColunas()
        this.sortItemsByCaption(1)

        await this.catalog.prepare()
        this.scope.catalogVisible = true
    }

    private async __prepareColunas() {
        const mgr = this.filterManager

        for (const [key, caption] of COLUNAS_NUMERO) {
            await this.add(new TcmPresenterForNumberFilter(this, new NumberFilterScope(), mgr[key]), caption, key)
        }
        for (const [key, caption] of COLUNAS_TEXTO) {
            await this.add(new TcmPresenterForStringFilter(this, new StringFilterScope(), mgr[key]), caption, key)
        }
        for (const [key, caption] of COLUNAS_DATA) {
            await this.add(new TcmPresenterForDateFilter(this, new DateFilterScope(), mgr[key]), caption, key)
        }
        for (const [key, caption] of COLUNAS_CNPJ) {
            await this.add(new TcmPresenterFilterForCnpj(this, new StringKeywordFilterScope(), mgr[key]), caption, key)
        }
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                mgr.matrizUfs,
                loadAllUFs
            ),
            'UF - Matriz',
            'matrizUfs'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                mgr.optanteSimples,
                async () => OPTANTE_SIMPLES_OPTIONS
            ),
            'Optante Simples',
            'optanteSimples'
        )
        await this.addSelect(
            new TcmPresenterForSelectFilter(this, new SelectFilterScope(), mgr.matrizFilial),
            'Matriz/Filial',
            MATRIZ_FILIAL_OPTIONS,
            'matrizFilial'
        )

        for (const [key, caption] of VET_COLUNAS_TEXTO) {
            const prop = this.__colunasTexto.get(key)!
            await this.add(
                new TcmPresenterForStringFilter(this, new StringFilterScope(), prop),
                caption,
                `colunas.${key}`
            )
        }
        for (const [key, caption] of VET_COLUNAS_LISTA) {
            const prop = this.__colunasLista.get(key)!
            await this.add(
                new TcmPresenterForStringMultiSelectFilter(
                    this,
                    new MultipleOptionStringKeywordFilterScope(),
                    prop,
                    async () => (await this.__loadOpcoes()).colunas?.[key] ?? []
                ),
                caption,
                `colunas.${key}`
            )
        }
    }

    protected override restoreDefaults() {
        this.__cnaeOrdem?.add({ mode: JunctionMode.OR, operator: SelectOperator.is, value: 'primario' })
    }

    protected override hasExtraFilter() {
        return (
            this.cnaeOrdem() !== 'primario' ||
            [
                this.__secundarias,
                this.__cnaePrincipal,
                this.__dominio,
                this.__fonteDominio,
                this.__email,
                this.__telefone,
                this.__midiaSocial,
                this.__palavras,
                ...this.__colunasTexto.values(),
                ...this.__colunasLista.values()
            ].some((prop) => prop.size > 0)
        )
    }

    protected override afterPublish() {
        this.catalog.refreshDirtyState()
    }

    cnaeOrdem(): CnaeOrdem {
        const incluir = new Set<string>()
        const excluir = new Set<string>()
        for (const filter of this.__cnaeOrdemProp.values()) {
            if (!filter.value) continue
            if (filter.operator === SelectOperator.is_not) {
                excluir.add(filter.value)
            } else if (filter.operator === SelectOperator.is) {
                incluir.add(filter.value)
            }
        }
        const posicoes = (incluir.size > 0 ? [...incluir] : ['primario', 'secundario']).filter((p) => !excluir.has(p))
        if (posicoes.length === 1) return posicoes[0] as CnaeOrdem
        return posicoes.length === 0 ? 'primario' : 'todos'
    }

    buildEmpresa(): EmpresaFilter {
        return this.filterManager.build()
    }

    buildCampos(): VetCamposFilter {
        return {
            secundarias: this.lista(this.__secundarias),
            palavras: this.lista(this.__palavras),
            cnaePrincipal: this.lista(this.__cnaePrincipal),
            dominio: this.lista(this.__dominio),
            fonteDominio: this.lista(this.__fonteDominio),
            email: this.lista(this.__email),
            telefone: this.lista(this.__telefone),
            midiaSocial: this.lista(this.__midiaSocial),
            colunas: this.__buildColunas()
        }
    }

    private __buildColunas(): VetFilter['colunas'] {
        const colunas: NonNullable<VetFilter['colunas']> = {}
        for (const props of [this.__colunasTexto, this.__colunasLista]) {
            for (const [key, prop] of props) {
                const filters = this.lista<StringFilter | MultipleStringKeywordFilter>(prop)
                if (filters) colunas[key] = filters
            }
        }
        return Object.keys(colunas).length > 0 ? colunas : undefined
    }

    buildSavedFilter(): EmpresaFilter {
        const data: Record<string, unknown> = { ...this.filterManager.build(), ...this.buildCampos() }
        if (this.cnaeOrdem() !== 'primario') data[CNAE_ORDEM_KEY] = this.lista(this.__cnaeOrdemProp)
        for (const key of Object.keys(data)) {
            if (data[key] === undefined) delete data[key]
        }
        return data as EmpresaFilter
    }

    async apply(filter: EmpresaFilter) {
        const data = filter as Record<string, unknown>
        this.applyItems(data)
        if (data[CNAE_ORDEM_KEY] === undefined) this.restoreDefaults()

        const localizacao = filter.localizacao
        if (localizacao) {
            this.filterManager.localizacaoCircles.apply(localizacao.circles)
            this.filterManager.localizacaoPolygons.apply(localizacao.polygons)
            this.filterManager.localizacaoRoutes.apply(localizacao.routes)
        }

        await this.owner.onSavedFilterApplied()
    }

    palavrasSelecionadas() {
        return this.selecionadasNaLista(this.__palavras)
    }

    togglePalavra(texto: string) {
        this.__palavrasPresenter && this.toggleNaLista(this.__palavrasPresenter, texto)
    }

    secundariasSelecionadas() {
        return this.selecionadasNaLista(this.__secundarias)
    }

    toggleSecundaria(codigo: string) {
        this.__secundariasPresenter && this.toggleNaLista(this.__secundariasPresenter, codigo)
    }

    private __loadOpcoes() {
        if (!this.__opcoes) {
            this.__opcoes = service.fetchCnpjOpcoes()
            this.__opcoes.catch(() => (this.__opcoes = undefined))
        }
        return this.__opcoes
    }

    private async __sugestoes(campo: 'razaoSocial' | 'nomeFantasia', texto: string) {
        try {
            return await service.fetchCnpjSugestoes(campo, texto, this.owner.buildFilter())
        } catch (caught) {
            LOG.error('fetchCnpjSugestoes', caught)
            return []
        }
    }
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Aba "Mercado Veterinário WEB" (Google Maps)
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

export type WebFilterHost = FilterPaneHost & {
    readonly app: MainPresenter
    onSavedFilterApplied(): Promise<void>
}

export class MvWebFilterPanePresenter extends MvFilterPanePresenter<WebFilterHost> {
    constructor(owner: WebFilterHost, scope: MvFilterPaneScope) {
        super(owner, scope)
        const app = owner.app
        this.catalog = new TcmPresenterForFilterCatalog(this, {
            app,
            module: 'mv-web',
            buildData: () => this.buildSavedFilter(),
            openForm: (mode, sourceId) => new MvFilterCatalogFormKeys(app).setMode(mode).setSourceId(sourceId).flip(),
            openShare: (sourceId) => new MvFilterCatalogShareKeys(app).setSourceId(sourceId).flip()
        })
    }

    readonly catalog: TcmPresenterForFilterCatalog

    private readonly __estabelecimento = this.newStringProperty()
    private readonly __endereco = this.newStringProperty()
    private readonly __categorias = this.newMultipleProperty()
    private readonly __ufs = this.newMultipleProperty()
    private readonly __municipios = this.newMultipleProperty()
    private readonly __nota = this.newNumberProperty()
    private readonly __avaliacoes = this.newNumberProperty()
    private readonly __aberto24h = this.newSelectProperty()
    private readonly __instagram = this.newSelectProperty()
    private readonly __palavras = this.newMultipleProperty()

    private __opcoes?: Promise<WebOpcoes>
    private __palavrasPresenter?: TcmPresenterForStringMultiSelectFilter

    override release() {
        this.catalog.release()
        super.release()
    }

    protected override async prepareFilters() {
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), this.__estabelecimento),
            'Estabelecimento',
            'estabelecimento'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__categorias,
                async () => (await this.__loadOpcoes()).categorias
            ),
            'Categoria',
            'categorias'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__ufs,
                loadAllUFs
            ),
            'UF',
            'ufs'
        )
        await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__municipios,
                async () => (await this.__loadOpcoes()).municipios
            ),
            'Município',
            'municipios'
        )
        await this.add(
            new TcmPresenterForStringFilter(this, new StringFilterScope(), this.__endereco),
            'Endereço',
            'endereco'
        )
        await this.add(new TcmPresenterForNumberFilter(this, new NumberFilterScope(), this.__nota), 'Nota', 'nota')
        await this.add(
            new TcmPresenterForNumberFilter(this, new NumberFilterScope(), this.__avaliacoes),
            'Total de Avaliações',
            'avaliacoes'
        )
        await this.addSelect(
            new TcmPresenterForSelectFilter(this, new SelectFilterScope(), this.__aberto24h),
            'Aberto 24h',
            SIM_NAO_OPTIONS,
            'aberto24h'
        )
        await this.addSelect(
            new TcmPresenterForSelectFilter(this, new SelectFilterScope(), this.__instagram),
            'Possui Instagram',
            SIM_NAO_OPTIONS,
            'instagram'
        )
        this.__palavrasPresenter = await this.add(
            new TcmPresenterForStringMultiSelectFilter(
                this,
                new MultipleOptionStringKeywordFilterScope(),
                this.__palavras,
                async () => (await this.__loadOpcoes()).palavras
            ),
            'Palavras em Destaque',
            'palavras'
        )

        await this.catalog.prepare()
        this.scope.catalogVisible = true
    }

    protected override afterPublish() {
        this.catalog.refreshDirtyState()
    }

    protected override hasExtraFilter() {
        return [
            this.__estabelecimento,
            this.__endereco,
            this.__categorias,
            this.__ufs,
            this.__municipios,
            this.__nota,
            this.__avaliacoes,
            this.__aberto24h,
            this.__instagram,
            this.__palavras
        ].some((prop) => prop.size > 0)
    }

    buildWebFilter(): WebFilter {
        return {
            estabelecimento: this.lista(this.__estabelecimento),
            endereco: this.lista(this.__endereco),
            categorias: this.lista(this.__categorias),
            ufs: this.lista(this.__ufs),
            municipios: this.lista(this.__municipios),
            nota: this.lista(this.__nota),
            avaliacoes: this.lista(this.__avaliacoes),
            aberto24h: this.lista(this.__aberto24h),
            instagram: this.lista(this.__instagram),
            palavras: this.lista(this.__palavras)
        }
    }

    buildSavedFilter(): EmpresaFilter {
        const data: Record<string, unknown> = { ...this.buildWebFilter() }
        for (const key of Object.keys(data)) {
            if (data[key] === undefined) delete data[key]
        }
        return data as EmpresaFilter
    }

    async apply(filter: EmpresaFilter) {
        this.applyItems(filter as Record<string, unknown>)
        await this.owner.onSavedFilterApplied()
    }

    palavrasSelecionadas() {
        return this.selecionadasNaLista(this.__palavras)
    }

    togglePalavra(texto: string) {
        this.__palavrasPresenter && this.toggleNaLista(this.__palavrasPresenter, texto)
    }

    private __loadOpcoes() {
        if (!this.__opcoes) {
            this.__opcoes = service.fetchWebOpcoes()
            this.__opcoes.catch(() => (this.__opcoes = undefined))
        }
        return this.__opcoes
    }
}
